/**
 * 🎯 V182.5: Age Verification Service
 * Manages 18+ age verification for Google Play compliance
 *
 * P1 hotfix: 24h browser-local denial cooldown (no schema change).
 * Cooldown is device/browser-scoped via localStorage — not proof of adulthood.
 */

import { supabase } from '@/integrations/supabase/client';

/** Exactly 24 hours from the most recent underage confirmation (local). */
export const AGE_DENIAL_COOLDOWN_MS = 24 * 60 * 60 * 1000;

const DENIAL_KEY_PREFIX = 'blissnet_age_denied_at:';

/** Reject timestamps more than 1 day in the future as malformed. */
const MAX_FUTURE_SKEW_MS = 24 * 60 * 60 * 1000;

export type AgeVerificationStatus = 'not_asked' | 'verified' | 'denied';

export function ageDenialStorageKey(userId: string): string {
  return `${DENIAL_KEY_PREFIX}${userId}`;
}

/**
 * Read and validate a user-scoped denial timestamp.
 * Removes only the malformed key when invalid; never clears other storage.
 */
export function getAgeDenialTimestamp(userId: string): number | null {
  if (typeof localStorage === 'undefined') return null;

  const key = ageDenialStorageKey(userId);
  let raw: string | null;
  try {
    raw = localStorage.getItem(key);
  } catch (error) {
    console.error('🎯 AgeVerification: Failed to read denial timestamp', error);
    return null;
  }

  if (raw === null || raw === undefined || raw === '') {
    return null;
  }

  const parsed = Number(raw);
  if (!Number.isFinite(parsed) || parsed < 0) {
    clearAgeDenialTimestamp(userId);
    return null;
  }

  const now = Date.now();
  if (parsed > now + MAX_FUTURE_SKEW_MS) {
    clearAgeDenialTimestamp(userId);
    return null;
  }

  return parsed;
}

export function setAgeDenialTimestamp(userId: string, atMs: number = Date.now()): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.setItem(ageDenialStorageKey(userId), String(atMs));
  } catch (error) {
    console.error('🎯 AgeVerification: Failed to save denial timestamp', error);
  }
}

export function clearAgeDenialTimestamp(userId: string): void {
  if (typeof localStorage === 'undefined') return;
  try {
    localStorage.removeItem(ageDenialStorageKey(userId));
  } catch (error) {
    console.error('🎯 AgeVerification: Failed to clear denial timestamp', error);
  }
}

/**
 * Whether the local 24h denial cooldown is still active for this user.
 * Expired or missing timestamps return false (and expired keys are removed).
 */
export function isAgeDenialCooldownActive(userId: string, nowMs: number = Date.now()): boolean {
  const deniedAt = getAgeDenialTimestamp(userId);
  if (deniedAt === null) return false;

  const elapsed = nowMs - deniedAt;
  if (elapsed < AGE_DENIAL_COOLDOWN_MS) {
    return true;
  }

  clearAgeDenialTimestamp(userId);
  return false;
}

/**
 * Resolve DB age_verified + local cooldown into a session age status.
 * - true → verified
 * - false + active local cooldown → denied
 * - false + expired/missing local timestamp → not_asked (safe recovery; must confirm Yes)
 * - null/undefined → not_asked
 */
export function resolveAgeStatusFromDbValue(
  ageVerified: boolean | null | undefined,
  userId: string,
  nowMs: number = Date.now()
): AgeVerificationStatus {
  if (ageVerified === true) {
    return 'verified';
  }

  if (ageVerified === false) {
    if (isAgeDenialCooldownActive(userId, nowMs)) {
      return 'denied';
    }
    return 'not_asked';
  }

  return 'not_asked';
}

export class AgeVerificationService {
  /**
   * Check if user has been asked about age
   * @param userId - User ID
   * @returns 'not_asked' | 'verified' | 'denied'
   */
  static async getVerificationStatus(userId: string): Promise<AgeVerificationStatus> {
    try {
      console.log('🎯 V182.5: Checking age verification status for user:', userId);

      // V205.3: Use .maybeSingle() to handle new users without profiles row
      const { data, error } = await supabase
        .from('profiles')
        .select('age_verified')
        .eq('user_id', userId)
        .maybeSingle();

      if (error) {
        console.error('🎯 V182.5: Error checking verification status:', error);
        return 'not_asked';
      }

      // V205.3: No row found (new user) - treat as not_asked
      if (!data) {
        console.log('🎯 V205.3: No profile row found (new user) - not yet asked about age');
        return 'not_asked';
      }

      const status = resolveAgeStatusFromDbValue(data.age_verified, userId);
      if (status === 'verified') {
        console.log('🎯 V182.5: User verified as 18+');
      } else if (status === 'denied') {
        console.log('🎯 V182.5: User denied (under 18) — cooldown active');
      } else {
        console.log('🎯 V182.5: User not yet asked about age (or cooldown expired / no local denial)');
      }
      return status;
    } catch (error) {
      console.error('🎯 V182.5: Exception checking verification status:', error);
      return 'not_asked';
    }
  }

  /**
   * Mark user as verified (18+)
   * @param userId - User ID
   */
  static async markAsVerified(userId: string): Promise<boolean> {
    try {
      console.log('🎯 V182.5: Marking user as verified 18+');

      const { data, error } = await supabase
        .from('profiles')
        .update({ age_verified: true })
        .eq('user_id', userId)
        .select('user_id');

      if (error) {
        console.error('🎯 V182.5: Error marking as verified:', error);
        return false;
      }

      if (!data || data.length === 0) {
        console.error('🎯 V182.5: Age verify update affected 0 profiles rows (no profile for user)');
        return false;
      }

      clearAgeDenialTimestamp(userId);
      console.log('🎯 V182.5: User marked as verified ✅');
      return true;
    } catch (error) {
      console.error('🎯 V182.5: Exception marking as verified:', error);
      return false;
    }
  }

  /**
   * Record underage confirmation: local cooldown timestamp + best-effort DB false.
   * Does not sign out or navigate.
   */
  static async markAsDenied(userId: string): Promise<boolean> {
    try {
      console.log('🎯 V182.5: Marking user as denied (under 18)');

      setAgeDenialTimestamp(userId);

      const { data, error } = await supabase
        .from('profiles')
        .update({ age_verified: false })
        .eq('user_id', userId)
        .select('user_id');

      if (error) {
        console.error('🎯 V182.5: Error marking as denied:', error);
        // Local cooldown still applied — fail closed for this session
        return false;
      }

      if (!data || data.length === 0) {
        console.error('🎯 V182.5: Age deny update affected 0 profiles rows (no profile for user)');
        // Local cooldown still applied — fail closed for this session
        return false;
      }

      console.log('🎯 V182.5: User marked as denied (under 18) ❌');
      return true;
    } catch (error) {
      console.error('🎯 V182.5: Exception marking as denied:', error);
      setAgeDenialTimestamp(userId);
      return false;
    }
  }

  /**
   * Sign out so the user can return to the public auth screen.
   * Does not clear denial timestamps or unrelated storage.
   */
  static async revokeAccess(_userId: string): Promise<boolean> {
    try {
      console.log('🎯 V182.5: Revoking access for user under 18');

      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error('🎯 V182.5: Error revoking access:', error);
        return false;
      }

      console.log('🎯 V182.5: User signed out successfully');
      return true;
    } catch (error) {
      console.error('🎯 V182.5: Error revoking access:', error);
      return false;
    }
  }
}
